import {
  GetAudiologistsOfSupplierParams,
  getGetAudiologistsOfSupplierQueryKey,
  PaginatedConnectedAudiologistListItemsDTO,
  useDeleteConnectedAudiologist,
  useGetAudiologistsOfSupplier,
} from "@bvhi/backend-api";
import { Card, CardContent, GridPaginationModel, GridSortModel } from "@bvhi/ui-base";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { forwardRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { BVHIDataGrid } from "../../../../../../../components/datagrid/bvhi-datagrid";
import { useAuthContext } from "../../../../../../../context/use-auth-context";
import { SecuredDeleteDialog } from "../../../../../../../dialogs/secured-delete-dialog";
import { useToast } from "../../../../../../../hooks/use-toast";
import { Route } from "../../../../../../../routes/_main/app/audiologists.index";
import { toNarrowedTypeOrNull } from "../../../../../../../utils/ts";
import { useAudiologistsDataGridDefinitions } from "../../hooks/use-audiologists-data-grid-definitions";
import { useAudiologistsSortParams } from "../../hooks/use-audiologists-sort-params";

interface Props {
  requestParams: GetAudiologistsOfSupplierParams;
  updateCallback: (paginationModel: GridPaginationModel, sortModel: GridSortModel) => void;
}

export const AudiologistsDataGrid = forwardRef<HTMLDivElement, Props>(({ requestParams, updateCallback }, ref) => {
  const { t } = useTranslation(["audiologists", "dialogs"]);
  const { currentUser } = useAuthContext();
  const queryClient = useQueryClient();
  const { mutate: deleteConnectedAudiologist } = useDeleteConnectedAudiologist();
  const { sort, page, size } = Route.useSearch();
  const { getSortModelForBackendString } = useAudiologistsSortParams();

  const { data, isFetching } = useGetAudiologistsOfSupplier(currentUser?.organisation?.id ?? "", requestParams, {
    query: { throwOnError: false, placeholderData: keepPreviousData },
  });

  const [audiologistIdToDelete, setAudiologistIdToDelete] = useState<string | null>(null);
  const { pushDeleteSuccessToast, pushDeleteErrorToast } = useToast();

  const handleDelete = useCallback(
    (audiologistId: string) => {
      setAudiologistIdToDelete(audiologistId);
    },
    [setAudiologistIdToDelete]
  );

  const audiologists = toNarrowedTypeOrNull<PaginatedConnectedAudiologistListItemsDTO>(data?.data, "numberOfItems");
  const { columns } = useAudiologistsDataGridDefinitions(handleDelete, audiologists?.catalogs ?? []);

  const handleCornfirmDialogClose = useCallback(
    (confirmed: boolean) => {
      if (!confirmed || !audiologistIdToDelete) {
        setAudiologistIdToDelete(null);
        return;
      }
      deleteConnectedAudiologist(
        { audiologistId: audiologistIdToDelete, supplierId: currentUser?.organisation?.id ?? "" },
        {
          onSuccess: () => {
            pushDeleteSuccessToast(t("audiologists:audiologists.entity"));
          },
          onError: () => {
            pushDeleteErrorToast(t("audiologists:audiologists.entity"));
          },
          onSettled: () => {
            setAudiologistIdToDelete(null);
            void queryClient.invalidateQueries({ queryKey: getGetAudiologistsOfSupplierQueryKey(currentUser?.organisation?.id ?? "") });
          },
        }
      );
    },
    [
      deleteConnectedAudiologist,
      pushDeleteSuccessToast,
      pushDeleteErrorToast,
      queryClient,
      t,
      audiologistIdToDelete,
      currentUser,
      setAudiologistIdToDelete,
    ]
  );

  return (
    <div ref={ref}>
      <Card sx={{ minHeight: 300 }}>
        <CardContent restrictContentWidth={false}>
          <BVHIDataGrid
            columns={columns}
            getRowHeight={() => "auto"}
            initalPaginationModel={{ page: page ? page : 0, pageSize: size ? size : 25 }}
            initialSortModel={getSortModelForBackendString(sort && sort.length >= 1 ? sort[0] : "")}
            loading={isFetching}
            rowCount={audiologists?.numberOfItems ?? -1}
            rows={audiologists?.payload ?? []}
            totalPages={audiologists?.numberOfPages ?? 0}
            updateCallback={updateCallback}
          />
        </CardContent>
      </Card>
      <SecuredDeleteDialog
        onClose={handleCornfirmDialogClose}
        open={audiologistIdToDelete !== null}
        overwrite={{
          title: t("dialogs:dialogs.delete.audiologist.title"),
          confirmationCode: t("audiologists:delete.action.confirmation.code"),
        }}
      />
    </div>
  );
});
AudiologistsDataGrid.displayName = "AudiologistsDataGrid";
